import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  ApartmentOutlined,
  BadgeOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  EditOutlined,
  EventRepeatOutlined,
  ScheduleOutlined,
  StorageOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { ViewActionFooter } from "../Shared/ViewActionFooter";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { LoaderWithoutHeight } from "components/Loader";
import { tooltipStyleProps } from "services/constants/Styles";
import { PayInputActivityLog } from "../Shared/PayInputActivityLog";
import { DateField, SelectField, TextField, toOptions } from "../Shared/fieldInputs";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { formatDate, formatExactQty, formatMoney } from "../Shared/formatters";
import { designColors, designShell } from "services/constants/designTokens";
import { BASIS_OPTIONS, validateForm, previewLeaveEarning } from "./leaveEarningsData";
import { useLeaveEarningTypes } from "./useLeaveEarningTypes";
import { useShiftSchedules } from "../ShiftEarnings/useShiftSchedules";
import { loadShiftFormOptions } from "../ShiftEarnings/shiftEarningsApi";
import { useSalaryRateAutofill } from "../Shared/useSalaryRateAutofill";

// Label with the red required asterisk.
const required = (label) => (
  <>
    {label} <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
  </>
);

// Pay Inputs > Leave panels, same layout as the Shift Earnings panels.
// Add + Edit. `initial` is a form object (see emptyForm / rowToForm).
// `source` is the entry's source when editing: Manual and Import entries
// are fully editable; Attendance entries come from the leave command, so
// only their Payroll Cycle can be changed here.
export const LeaveEarningFormPanel = ({
  open,
  mode,
  initial,
  source,
  onClose,
  onSave,
  expanded,
  onToggleExpand,
  saving,
}) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const [options, setOptions] = useState({ employees: [], shifts: [], errors: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setOptionsLoading(true);
    setOptions({ employees: [], shifts: [], errors: [] });
    loadShiftFormOptions(domain, topbarCompanyId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setOptions(data); })
      .catch((error) => { if (!controller.signal.aborted) setOptions({ employees: [], shifts: [], errors: [error.message] }); })
      .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    return () => controller.abort();
  }, [open, domain, topbarCompanyId, retry]);
  const locked = mode === "edit" && source === "Attendance";
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial);
      setShowErrors(false);
    }
  }, [open, initial]);

  // Leave Type options from GET payroll/earning/leave/earning/types/.
  const { types: leaveTypes, loading: leaveTypesLoading } = useLeaveEarningTypes(domain, open);
  // Add starts on a type the server offers (the default may not be one).
  useEffect(() => {
    if (!open || mode !== "add" || leaveTypesLoading) return;
    setForm((current) =>
      current && !leaveTypes.includes(current.earning_type)
        ? { ...current, earning_type: leaveTypes.includes("Paid Leave") ? "Paid Leave" : leaveTypes[0] }
        : current,
    );
  }, [open, mode, leaveTypes, leaveTypesLoading]);

  const errors = useMemo(() => (form ? validateForm(form, mode === "edit" ? initial : null, leaveTypes) : {}), [form, mode, initial, leaveTypes]);
  const totals = useMemo(() => (form ? previewLeaveEarning(form, mode === "edit" ? initial : null) : null), [form, mode, initial]);

  const { schedules, error: scheduleError, loading: schedulesLoading, retry: retrySchedules } = useShiftSchedules(domain, [form?.employee_code], open);

  // Rate auto-fill, same rule as Shift Earnings: on Add, and on Edit of
  // Manual / Import entries. Attendance-sourced (locked) entries keep their rate.
  const employeeUserId = options.employees.find((option) => option.value === form?.employee_code)?.user_id || "";
  useSalaryRateAutofill({
    domain,
    enabled: open && !locked,
    userId: employeeUserId,
    payScheduleId: form?.pay_schedule_id,
    date: form?.date,
    basis: form?.basis,
    setForm,
  });

  if (!form) return null;

  const employeeOptions = [...options.employees];
  if (form.employee_code && !employeeOptions.some((option) => option.value === form.employee_code)) employeeOptions.push({ value: form.employee_code, id: form.employee_code, employee_id: form.employee_code, label: initial.employee_name || form.employee_code, image: initial.image_url || "", image_url: initial.image_url || "" });
  const selectedEmployee = employeeOptions.find((option) => option.value === form.employee_code) ?? null;
  const shiftOptions = [...options.shifts];
  if (form.shift_name && !shiftOptions.some((option) => option.value === form.shift_name)) shiftOptions.push({ value: form.shift_name, label: form.shift_name });
  const cycleOptions = [...schedules];
  if (form.pay_schedule_id && !cycleOptions.some((option) => option.value === form.pay_schedule_id)) cycleOptions.push({ value: form.pay_schedule_id, label: initial.payroll_cycle || form.pay_schedule_id });
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value, ...(key === "employee_code" ? { pay_schedule_id: "" } : {}) }));
  const visibleError = (key) => (showErrors ? errors[key] : undefined);

  const handleSave = () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      return;
    }
    onSave(form);
  };

  const numberInput = (key) => (
    <TextField
      type="number"
      value={form[key]}
      disabled={locked || saving}
      error={visibleError(key)}
      onChange={set(key)}
    />
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={saving ? undefined : onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title={mode === "edit" ? "Edit Leave Earnings" : "Add Leave Earnings"}
      subtitle={
        locked
          ? "This entry comes from approved leave (Attendance). Only the payroll cycle can be changed."
          : mode === "edit"
            ? "Update this entry. The payable amount is recalculated."
            : "Manually add a leave earnings entry for an employee."
      }
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={saving ? undefined : onClose}
          primaryLabel={mode === "edit" ? "Save Changes" : "Add"}
          primaryProps={{ onClick: handleSave, loading: saving, disabled: saving || optionsLoading || schedulesLoading }}
        />
      }
    >
      {/* Employees and shifts load first; the form shows once they are in. */}
      {optionsLoading ? <LoaderWithoutHeight info="Loading..." /> : (
      <Mui.Stack spacing={2}>
        {options.errors.length ? <Mui.Alert severity="warning" action={<Mui.Button onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{options.errors.join(" ")}</Mui.Alert> : null}
        {scheduleError ? <Mui.Alert severity="warning" action={<Mui.Button onClick={retrySchedules}>Retry</Mui.Button>}>{scheduleError}</Mui.Alert> : null}
        {locked ? (
          <RequestInfoBanner
            title="Leave earning details (read only)"
            body="This record is created from approved leave (Attendance). Only the payroll cycle can be changed."
          />
        ) : null}
        <FormSection title="Employee">
          <Mui.Box sx={{
            gridColumn: "1 / -1",
            ...(mode === "edit" ? {
              "& .MuiInputBase-input.Mui-disabled": {
                color: designColors.slate900,
                WebkitTextFillColor: designColors.slate900,
                opacity: 1,
              },
            } : {}),
          }}>
            <Field
              label={required("Employee")}
              helper="Employee on leave. Search by name or employee code."
              error={visibleError("employee_code")}
            >
              <CustomAutoCompleteWithLogo
                dataList={employeeOptions}
                selectedValue={selectedEmployee}
                updateDataFunction={(event, option) => set("employee_code")(option?.value ?? "")}
                placeHolder="Select employee"
                textBoxError={Boolean(visibleError("employee_code"))}
                disableAction={mode === "edit" || saving || optionsLoading}
                showAvathar={!selectedEmployee}
              />
            </Field>
          </Mui.Box>
        </FormSection>
        {/* Picked employee's card on its own (not inside the white section
            card, which washed out its teal background), same as Team
            Workspace Approvals' Apply panels. */}
        {selectedEmployee ? (
          <SelectedEmployeeHeader
            selectedUser={{
              ...selectedEmployee,
              department_name: selectedEmployee.department_name || initial.department_name,
              designation: selectedEmployee.designation || initial.designation,
            }}
          />
        ) : null}

        <FormSection title="Payroll Details">
          {/* Helpers on paired fields stay one line (≤ ~30 characters): a
              longer one wraps and pushes its input below its neighbour's. */}
          <Field label={required("Payroll Cycle")} helper="Payroll run that pays this.">
            <SelectField
              value={form.pay_schedule_id}
              onChange={set("pay_schedule_id")}
              options={[{ value: "", label: mode === "edit" ? "Unassigned" : "Automatic" }, ...cycleOptions]}
              disabled={saving || schedulesLoading || !form.employee_code}
              placeholder={schedulesLoading ? "Loading payroll cycles..." : "Select payroll cycle"}
            />
          </Field>
          <Field label="Source" helper="Where this entry came from.">
            <TextField
              value={mode === "edit" ? (locked ? "Attendance" : source || "–") : "Manual"}
              disabled
              onChange={() => {}}
            />
          </Field>
        </FormSection>

        <FormSection title="Leave Earning Details">
          {/* The date can't be changed once saved (the server refuses it). */}
          <Field label={required("Date")} helper={mode === "edit" ? "Can't be changed once saved." : "Date of the leave."} error={visibleError("date")}>
            <DateField
              value={form.date}
              disabled={locked || saving || mode === "edit"}
              error={visibleError("date")}
              onChange={set("date")}
            />
          </Field>
          <Field label={required("Leave Type")} helper="Leave type paid in payroll." error={visibleError("earning_type")}>
            <SelectField
              value={form.earning_type}
              disabled={locked || saving}
              onChange={set("earning_type")}
              error={visibleError("earning_type")}
              options={toOptions([...new Set([...leaveTypes, form.earning_type].filter(Boolean))])}
              placeholder={leaveTypesLoading ? "Loading leave types..." : "Select leave type"}
            />
          </Field>
          <Field label="Shift" helper="Rostered shift (optional)." error={visibleError("shift_name")}>
            <SelectField
              value={form.shift_name}
              disabled={locked || saving}
              onChange={set("shift_name")}
              error={visibleError("shift_name")}
              options={[{ value: "", label: "No shift" }, ...shiftOptions]}
              placeholder="Select shift"
            />
          </Field>
          <Field label={required("Basis")} helper="Pay per shift (day) or hour." error={visibleError("basis")}>
            <SelectField
              value={form.basis}
              disabled={locked || saving}
              onChange={set("basis")}
              error={visibleError("basis")}
              options={BASIS_OPTIONS}
              placeholder="Select basis"
            />
          </Field>
          <Field label={required("Credited Quantity")} helper="Shifts (0.5 = half) or hours." error={visibleError("quantity")}>
            {numberInput("quantity")}
          </Field>
          <Field label={required("Rate (₹)")} helper="Paid per shift or per hour." error={visibleError("rate")}>
            {numberInput("rate")}
          </Field>
          <Field label={required("Factor")} helper="Multiplier (1 = normal pay)." error={visibleError("payment_factor")}>
            {numberInput("payment_factor")}
          </Field>
          <Field label="Payable Amount (₹)" helper="Quantity × rate × factor.">
            <TextField value={formatMoney(totals.payable_amount)} disabled onChange={() => {}} />
          </Field>
        </FormSection>

      </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};

const money = (value) => (value == null ? "–" : formatMoney(value));
const payableQty = (row) => (Number(row.quantity) || 0) * (Number(row.payment_factor) || 0);

// showApproval: false when Pay Input Configuration has no approval step, so
// the status chip and Approval Status row are hidden (same as Shift Earnings).
export const LeaveEarningViewPanel = ({ open, row, onClose, onEdit, onDelete, onStatus, canEdit, showApproval = true, expanded, onToggleExpand, domain, busy }) => {
  const [tab, setTab] = useState("info");
  // Each record opens on its Information tab.
  useEffect(() => setTab("info"), [row?.id]);

  if (!row) return null;
  const { indigo, blue, green, orange, purple, pink } = detailAccentPalette;
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Leave Earning"
      subtitle={`Leave earning details · ${formatDate(row.date)}`}
      bodySx={panelBodySx}
      headerActions={
        canEdit ? (
          <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
            <span>
              <Mui.IconButton
                size="small"
                aria-label="Edit"
                disabled={busy}
                onClick={() => onEdit(row)}
                sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
              >
                <EditOutlined fontSize="small" />
              </Mui.IconButton>
            </span>
          </Mui.Tooltip>
        ) : null
      }
      // Delete sits in the footer as a full-width button, like the Cancel
      // action on Team Workspace Approvals' detail panels.
      footer={
        canEdit ? (
          <ViewActionFooter
            approval={showApproval ? { status: row.status, disabled: busy, onApprove: onStatus && (() => onStatus(row, "Approved")), onReject: onStatus && (() => onStatus(row, "Rejected")) } : null}
            recordAction={{ label: "Delete Leave Earning", onClick: () => onDelete(row), disabled: busy }}
          />
        ) : null
      }
    >
      {/* Employee card, same as Team Workspace Approvals' view panels. */}
      <Mui.Box sx={{ mb: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: row.image_url,
            employee_id: row.employee_code,
            user_name: row.employee_name,
            department_name: row.department_name || row.department,
            designation: row.designation_name || row.designation,
          }}
          status={showApproval ? row.status : undefined}
        />
      </Mui.Box>
      <Mui.Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="fullWidth"
        sx={{
          ...designShell.panelTabs,
          mt: 0,
          mb: 2,
          borderBottom: `1px solid ${designColors.slate200}`,
        }}
      >
        <Mui.Tab label="Information" value="info" />
        <Mui.Tab label="Activity Log" value="audit" />
      </Mui.Tabs>
      {tab === "audit" ? (
        <PayInputActivityLog domain={domain} type="LEAVE" recordId={row.id} refreshKey={row.modifiedat} />
      ) : (
        <Mui.Stack spacing={2}>
          <RequestDetailsSection
            title="LEAVE DETAILS"
            fields={[
              { icon: CalendarMonthOutlined, label: "Date", value: formatDate(row.date), accent: indigo },
              { icon: CategoryOutlined, label: "Leave Type", value: row.earning_type || "–", accent: pink },
              { icon: ScheduleOutlined, label: "Shift", value: row.shift_name || "–", accent: blue },
              { icon: BadgeOutlined, label: "Basis", value: row.basis || "–", accent: purple },
            ]}
          />
          <RequestDetailsSection
            title="CALCULATION"
            fields={[
              {
                icon: StorageOutlined,
                label: "Credited Qty × Factor = Payable Qty",
                value: `${formatExactQty(row.quantity)} × ${formatExactQty(row.payment_factor)} = ${formatExactQty(payableQty(row))}`,
                accent: green,
              },
              {
                icon: StorageOutlined,
                label: "Payable Qty × Rate = Earning Amount",
                value: `${formatExactQty(payableQty(row))} × ${money(row.rate)} = ${money(row.earning_amount)}`,
                accent: orange,
              },
              { icon: StorageOutlined, label: "Gross Pay", value: money(row.gross_pay), accent: orange },
              { icon: StorageOutlined, label: "Net Pay", value: money(row.net_pay), accent: orange },
            ]}
          />
          <RequestDetailsSection
            title="PAYROLL"
            fields={[
              ...(row.company_name ? [{ icon: ApartmentOutlined, label: "Company", value: row.company_name, accent: indigo }] : []),
              { icon: EventRepeatOutlined, label: "Payroll Cycle", value: row.payroll_cycle || "–", accent: indigo },
              ...(showApproval ? [{ icon: TaskAltOutlined, label: "Approval Status", value: row.status || "–", accent: blue }] : []),
              { icon: StorageOutlined, label: "Source", value: row.source || "–", accent: purple },
            ]}
          />
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
